import { useCallback, useEffect, useRef } from "react";

/**
 * Holds an object that accumulates samples about one thing, and replaces it when
 * that thing changes.
 *
 * A tracker's counters only mean something for the run they were taken in. Reused
 * across a device change or a reattempt, they would judge the new run on the old
 * one's samples, so the tracker is thrown away and built again rather than reset
 * in place.
 *
 * @param create Builds a fresh tracker. Read at the moment of building, so it
 * may close over current props.
 * @param watching Identifies the thing being watched — a device id, a dialogue
 * id. A new value replaces the tracker and calls `onRestart`.
 * @param onRestart Clears whatever the caller derived from the old tracker. Also
 * called by `restart`.
 */
export function useSampleTracker<T>(
  create: () => T,
  watching: string | undefined,
  onRestart?: () => void
): {
  /** The current tracker. Never null — one is built on the first render. */
  tracker: () => T;
  /** Throws the tracker away and starts counting again. */
  restart: () => void;
} {
  const createRef = useRef(create);
  createRef.current = create;
  const onRestartRef = useRef(onRestart);
  onRestartRef.current = onRestart;

  const trackerRef = useRef<T | null>(null);
  trackerRef.current ??= create();

  const restart = useCallback(() => {
    trackerRef.current = createRef.current();
    onRestartRef.current?.();
  }, []);

  // Compared rather than used as a dependency, so the first render's tracker is
  // kept instead of being built and immediately replaced.
  const watched = useRef(watching);

  useEffect(() => {
    if (watched.current === watching) return;
    watched.current = watching;
    restart();
  }, [watching, restart]);

  const tracker = useCallback(() => trackerRef.current!, []);

  return { tracker, restart };
}
